import React, { memo, useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { useRouter } from 'next/router';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { useDevice } from '@bonnet/next/device';

import {
    Button, Col,
    Text,
} from 'reaxl';
import {
    sendClick,
    sendImpressions,
} from 'reaxl-analytics';
import { PromoCard } from 'reaxl-listing';

import { inlineFilterPromoClick, inlineFilterPromoImpression } from '@/analytics/srp/srpSearchEvents';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpStrongAttributesDuck from '@/ducks/srp/srpStrongAttributesDuck.js';
import srpSuggestedFiltersDuck from '@/ducks/srp/srpSuggestedFiltersDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import TruckCabSizeFilter from '@/components/filters/TruckCabSizeFilter';

/**
 * render inline filter promo card body in priority order
 * 1) Strong Attribute filter
 * 2) Truck cab size filter
 * 3) SUV 3rd row filter
 * 4) Suggested filter
 */
function InlineFilterPromoCard() {
    const router = useRouter();

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const navigateToSrp = useSrpNavigation();

    const firstSuggestedFilter = useSelector(srpSuggestedFiltersDuck.selectors.getFirstSuggestion);
    const strongAttributeFilter = useSelector(srpStrongAttributesDuck.selectors.getFirstStrongAttributesFilter);
    const hasStrongAttributes = Object.keys(strongAttributeFilter).length > 0;
    const suggestedFilter = hasStrongAttributes ? strongAttributeFilter : firstSuggestedFilter;

    const isTruckSelected = useSelector(srpFiltersDuck.selectors.isTruckSelected);
    const isSUVSelected = useSelector(srpFiltersDuck.selectors.isSUVSelected);
    const featureFilterOptions = useSelector(srpFiltersDuck.selectors.getFeatureFilterOptions);
    const thirdRowFilterOptions = featureFilterOptions?.options?.find((option) => option.value === '1306');

    const featureFilterValues = useSelector(srpFiltersDuck.selectors.getFeatureFilterValues);
    const cabSizeOptions = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeOptions);
    const cabSizeValues = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeValue);

    const prevUrlRef = useRef(null);

    const thirdRowApplied = featureFilterValues.includes('1306');
    const cabSizeApplied = cabSizeValues.length;
    const isTruckCard = !hasStrongAttributes && isTruckSelected && !cabSizeApplied && !_isEmpty(cabSizeOptions);
    const isSUVCard = !hasStrongAttributes && (!isTruckSelected || cabSizeApplied || _isEmpty(cabSizeOptions))
        && isSUVSelected && !thirdRowApplied && !_isEmpty(thirdRowFilterOptions);

    const isSuggestedFilter = hasStrongAttributes || (!isTruckCard && !isSUVCard);

    let filterDataCmp = hasStrongAttributes ? 'filter-inline-strong' : 'filter-inline-suggested';
    if (isTruckCard) {
        filterDataCmp = 'filter-inline-truck';
    } else if (isSUVCard) {
        filterDataCmp = 'filter-inline-suv';
    }

    useEffect(() => {
        const currentUrl = router.asPath;

        // To make sure the impression only fires 1 time after card re-renders based on URL changes
        if (prevUrlRef.current !== currentUrl) {
            sendImpressions({
                name: inlineFilterPromoImpression,
                data: {
                    pixallData: {
                        cmp: filterDataCmp,
                    },
                },
            });

            prevUrlRef.current = currentUrl;
        }
    }, [filterDataCmp, router.asPath]);

    const handleButtonClick = useCallback(async (event, filterValue) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        sendClick(inlineFilterPromoClick, event, {
            pixallData: {
                cmp: filterDataCmp,
            },
        });

        await navigateToSrp({
            resetPagination: true,
            scroll: !isXs,
            scrollToResultsCount: isXs,
            isNewSearch: true,
            filtersValues: filterValue,
            action: 'merge',
        });
    }, [navigateToSrp, isXs, filterDataCmp]);

    const callToAction = (filterValue) => (isTruckCard ? (
        <TruckCabSizeFilter
            enableDropdown
            onOptionChange={(event) => handleButtonClick(event, { bodyStyleSubtypeCode: event?.target?.value })}
        />
    ) : (
        <Button
            bsSize="small"
            bsStyle="primary"
            onClick={(event) => handleButtonClick(event, filterValue)}
            className="margin-top-3"
        >
            Yes, Update My Search
        </Button>
    ));

    const renderCardBody = (promptText, filterValue) => (
        <>
            {isSuggestedFilter && (
                <Text
                    className="margin-top-3"
                    color="primary"
                    componentClass="div"
                >
                    Only interested in vehicles that are
                </Text>
            )}
            <Text
                className={clsx({ 'margin-top-3': !isSuggestedFilter })}
                color="primary"
                componentClass="div"
                size={400}
                weight="bold"
            >
                {promptText}
            </Text>

            {callToAction(filterValue)}
        </>
    );

    const shouldRenderCard = ((suggestedFilter?.name && suggestedFilter?.codes)) || isTruckCard || isSUVCard;

    return shouldRenderCard && (
        <Col
            componentClass={PromoCard}
            xs={12}
            sm={4}
            className="display-flex"
            data-cmp={filterDataCmp}
            imgSrc="https://www.autotrader.com/cm-api/content/static/img/icon/promocard/suggested_chat.png"
            imgHeight="40"
            panelClassName="bg-brand-secondary"
        >
            {isSuggestedFilter && renderCardBody(`${suggestedFilter.name}?`, suggestedFilter.codes)}
            {isTruckCard ? renderCardBody('How many passenger seats do you need in your truck?', {}) : null}
            {isSUVCard ? renderCardBody('Do you need 3rd row seating?', { featureCode: ['1306'] }) : null}
        </Col>
    );
}

export default memo(InlineFilterPromoCard);
